import { Analytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analyticsPromise } from '../firebase';

let analytics: Analytics | null = null;

// Initialize analytics instance
analyticsPromise.then(instance => {
    analytics = instance;
});

export const analyticsApi = {
    /**
     * Identify the current user after login.
     * Sets user ID and properties for all subsequent events.
     */
    identifyUser(uid: string, isAdmin: boolean) {
        if (!analytics) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Analytics not initialized, identifyUser dropped');
            }
            return;
        }
        try {
            setUserId(analytics, uid);
            setUserProperties(analytics, {
                is_admin: isAdmin.toString(),
            });
        } catch (error) {
            console.warn('Analytics identifyUser failed:', error);
        }
    },

    /**
     * Clear user identity on logout.
     */
    clearUser() {
        if (!analytics) return;
        try {
            setUserId(analytics, null);
        } catch (error) {
            console.warn('Analytics clearUser failed:', error);
        }
    },

    /**
     * Log a custom event with optional parameters.
     * Use snake_case for event names and parameter keys.
     */
    trackEvent(eventName: string, params?: Record<string, string | number | boolean>) {
        if (!analytics) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Analytics not initialized, event dropped:', eventName);
            }
            return;
        }
        try {
            logEvent(analytics, eventName, params);
        } catch (error) {
            console.warn('Analytics trackEvent failed:', eventName, error);
        }
    },

    // ========================================
    // Domain-specific event helpers
    // ========================================

    /** Track when a work order is created */
    trackWorkOrderCreated(buildingKey: string, category: string) {
        this.trackEvent('work_order_created', { building_key: buildingKey, category });
    },

    /** Track when a work order is completed */
    trackWorkOrderCompleted(workOrderKey: string) {
        this.trackEvent('work_order_completed', { work_order_key: workOrderKey });
    },

    /** Track when an inspection is started */
    trackInspectionStarted(templateId: string, buildingKey: string) {
        this.trackEvent('inspection_started', { template_id: templateId, building_key: buildingKey });
    },

    /** Track when an inspection is completed */
    trackInspectionCompleted(templateId: string, componentCount: number) {
        this.trackEvent('inspection_completed', { template_id: templateId, component_count: componentCount });
    },

    /** Track when maintenance data is updated */
    trackMaintenanceUpdated(componentType: string) {
        this.trackEvent('maintenance_updated', { component_type: componentType });
    },

    /** Track when a building detail page is viewed */
    trackBuildingViewed(buildingKey: string) {
        this.trackEvent('building_viewed', { building_key: buildingKey });
    },

    /** Track when a report is generated */
    trackReportGenerated(reportType: string) {
        this.trackEvent('report_generated', { report_type: reportType });
    },

    /** Track when a photo is uploaded */
    trackPhotoUploaded(context: 'inspection' | 'work_order' | 'component' | 'building' | 'parking' | 'storage_unit' | 'unit') {
        this.trackEvent('photo_uploaded', { context });
    },
};
